import { Link } from 'react-router';

import { SectionHeading } from '@/components/home/SectionHeading';
import { Container } from '@/components/ui/Container';
import { FEATURES_SECTION, FEATURE_CARDS } from '@/data/homeContent';
import { featurePath } from '@/features/marketing/nav';

/**
 * The six platform cards.
 *
 * Two across on a phone, three from `md`, all six in a row from `lg` - the
 * arrangement the design shows. Six across leaves roughly 110px of text per
 * card on a 1024px laptop, which is why the copy is set small and tight: at
 * `text-sm` it would break "Products & inventory" over three lines and run the
 * description to eight.
 *
 * The description is justified, which is a narrow column to justify. `hyphens`
 * is what keeps it from opening rivers between two-word lines; the page is
 * `lang="en"`, so the browser has a dictionary to break on.
 */
export function FeaturesGrid() {
  return (
    // Less room below than above: the industry band that follows opens with a
    // tinted background of its own, which reads as a break without needing a
    // full section's worth of white space in front of it.
    <section aria-labelledby="home-features" className="pb-6 pt-8 sm:pb-8 sm:pt-10">
      <Container>
        <SectionHeading
          id="home-features"
          heading={FEATURES_SECTION.heading}
          subheading={FEATURES_SECTION.subheading}
          link={{ label: FEATURES_SECTION.linkLabel, to: FEATURES_SECTION.linkTo }}
        />

        <ul className="mt-6 grid grid-cols-2 gap-3 sm:mt-8 sm:gap-4 md:grid-cols-3 lg:grid-cols-6">
          {FEATURE_CARDS.map((card) => (
            <li key={card.slug} className="flex">
              <Link
                to={featurePath(card.slug)}
                className="group flex w-full flex-col rounded-2xl border border-line/80 bg-white p-4 shadow-tile transition-all hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-card"
              >
                {/* Icon and title share a row, so the card opens with one
                    object instead of two stacked ones. The icon drops to 24px
                    to pay for it: at six across there is only about 110px of
                    text here, and every pixel the icon takes comes straight
                    out of the title beside it. */}
                <div className="flex items-center gap-2">
                  <img
                    src={card.iconSrc}
                    alt=""
                    className="size-6 shrink-0 object-contain"
                    loading="lazy"
                    width={24}
                    height={24}
                  />
                  <h3 className="min-w-0 text-sm font-bold leading-snug tracking-tight text-ink transition-colors group-hover:text-brand-600">
                    {card.title}
                  </h3>
                </div>
                <p className="mt-3 text-xs sm:hyphens-auto sm:text-justify leading-relaxed text-muted">
                  {card.description}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      </Container>
    </section>
  );
}
